import { PrecacheController, PrecacheRoute } from "workbox-precaching";
import { NetworkOnly } from "workbox-strategies";
import { NavigationRoute, registerRoute, setDefaultHandler } from "workbox-routing";
import { Capacitor, Plugins } from "capacitor-core-v2";
import { Preferences } from "@capacitor/preferences";
import { EMPTY, catchError, delayWhen, filter, from, fromEvent, interval, lastValueFrom, map, switchMap, take, tap, timer } from "rxjs";
import { BaseSessionStorage, BaseStorage } from "@golden/utils";
import { Clipboard } from "@capacitor/clipboard";
import { Keyboard } from "@capacitor/keyboard";
import { Share } from "@capacitor/share";
import { StatusBar, Style } from "@capacitor/status-bar";
import { NowTime } from "@golden/gdk-base";
//#region src/sw.ts
const ENTRY_FILE = "index.html";
function initSW() {
	self.addEventListener("message", (event) => {
		if (event.data && event.data.type === "SKIP_WAITING") self.skipWaiting();
	});
	const precacheController = new PrecacheController({ plugins: [notCacheRedirectionAndErrorPlugin] });
	precacheController.precache(self.__WB_MANIFEST);
	const precacheRoute = new PrecacheRoute(precacheController);
	registerRoute(precacheRoute);
	const networkOnly = new NetworkOnly({
		networkTimeoutSeconds: 30,
		fetchOptions: { cache: "no-store" }
	});
	registerRoute(new NavigationRoute(precacheController.createHandlerBoundToURL(ENTRY_FILE), { denylist: [
		/maintenance\.html/,
		/browser-unsupported\.html/,
		/^\/bf/,
		/^\/survey/,
		/^\/ga\/api/
	] }));
	setDefaultHandler(networkOnly);
	registerRoute(({ url }) => url.pathname === "/20KB.png", networkOnly);
	registerRoute(({ url }) => url.pathname === "/ga/api/v1/domains", networkOnly);
	registerRoute(({ url }) => url.host === "im.qq.com", networkOnly);
}
const notCacheRedirectionAndErrorPlugin = { async cacheWillUpdate({ response }) {
	if (!response || response.redirected || response.status >= 400) return null;
	return response;
} };
//#endregion
//#region src/utils/detect.ts
function isNativeApp() {
	return false;
}
function isSharkBrowser() {
	return window.navigator.userAgent.includes("ga-meow-shark-browser");
}
function isPWA() {
	return !isNativeApp() && !isSharkBrowser() && !window.navigator.userAgent.includes("ga-meow") && !!(window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone || !!window.HwWebViewJSI && !window.HwDevice?.getOaid || window.location.search.includes("isPWA=1"));
}
async function getClientHintJsonString(clientHint) {
	const highEntropyValues = await clientHint.getHighEntropyValues([
		"model",
		"platformVersion",
		"fullVersionList"
	]);
	const result = {
		brands: clientHint.brands,
		mobile: clientHint.mobile,
		platform: clientHint.platform,
		highEntropyValues
	};
	return JSON.stringify(result);
}
const getNativePlatform = () => {
	return "web";
};
const isIOSDevice = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
const getSafariVersion = () => {
	const userAgent = navigator.userAgent;
	if (!(userAgent.includes("Safari/") && !/Chrome|Chromium|CriOS|Edg/.test(userAgent))) return null;
	const match = /Version\/(\d+)\.(\d+)/.exec(userAgent);
	if (match) return `${match[1]}.${match[2]}`;
	return null;
};
//#endregion
//#region src/plugins/setup.ts
const setBackGestures = async (active) => {
	throw new Error("Platform not supported");
};
const appLoaded = async () => {
	throw new Error("Platform not supported");
};
const getAppLinkHost = async () => {
	throw new Error("Platform not supported");
};
const getAppSign$1 = async () => {
	throw new Error("Platform not supported");
};
//#endregion
//#region src/storages/NativeStorage.ts
let migrationChecked = false;
let migrationPromise = null;
const NativeStorage = {
	async get(options) {
		await this.migrateIfNeeded();
		return await Preferences.get(options);
	},
	async set(options) {
		await this.migrateIfNeeded();
		await Preferences.set(options);
	},
	async remove(options) {
		await this.migrateIfNeeded();
		await Preferences.remove(options);
	},
	async clear() {
		await this.migrateIfNeeded();
		await Preferences.clear();
	},
	async keys() {
		await this.migrateIfNeeded();
		return await Preferences.keys();
	},
	async migrateIfNeeded() {
		if (migrationChecked) return;
		if (migrationPromise) return await migrationPromise;
		migrationPromise = (async () => {
			try {
				const { value } = await Preferences.get({ key: "_CAP_MIGRATED" });
				if (value === "true") {
					migrationChecked = true;
					return;
				}
				const result = await Preferences.migrate();
				if (result.migrated.length > 0) console.log("NativeStorage migrated successfully:", result);
				await Preferences.set({
					key: "_CAP_MIGRATED",
					value: "true"
				});
				migrationChecked = true;
			} catch (e) {
				console.error("NativeStorage migrate failed", e);
			} finally {
				migrationPromise = null;
			}
		})();
		await migrationPromise;
	}
};
//#endregion
//#region src/storages/AgentLinkStorage.ts
const AGENT_LINK_KEY = "AGENT_LINK";
const KEY$1 = {
	ACCOUNT: `${AGENT_LINK_KEY}:ACCOUNT`,
	FROM: `${AGENT_LINK_KEY}:FROM`,
	LAST_UPDATE_AT: `${AGENT_LINK_KEY}:LAST_UPDATE_AT`
};
async function updateLatestSaveTime() {
	await NativeStorage.set({
		key: KEY$1.LAST_UPDATE_AT,
		value: Date.now().toString()
	});
}
async function isExpired() {
	const lastUpdateAt = await NativeStorage.get({ key: KEY$1.LAST_UPDATE_AT }).catch(() => ({ value: null }));
	if (!lastUpdateAt.value) return true;
	return Date.now() - Number(lastUpdateAt.value) > 36e5;
}
const AgentLinkStorage = {
	async getAccount() {
		if (await isExpired()) {
			await this.clear();
			return null;
		}
		return (await NativeStorage.get({ key: KEY$1.ACCOUNT }).catch(() => ({ value: null }))).value;
	},
	async setAccount(value) {
		await NativeStorage.set({
			key: KEY$1.ACCOUNT,
			value
		});
		await updateLatestSaveTime();
	},
	async getFrom() {
		return (await NativeStorage.get({ key: KEY$1.FROM }).catch(() => ({ value: null }))).value;
	},
	async setFrom(value) {
		await NativeStorage.set({
			key: KEY$1.FROM,
			value
		});
	},
	async clear() {
		await NativeStorage.remove({ key: KEY$1.ACCOUNT });
		await NativeStorage.remove({ key: KEY$1.FROM });
		await NativeStorage.remove({ key: KEY$1.LAST_UPDATE_AT });
	}
};
//#endregion
//#region src/modules/appAgentLinkGuard.ts
const appAgentLinkGuard = (onSuccess, onFailure) => {
	if (!isNativeApp()) return;
	throw new Error("Platform not supported");
};
async function handleAgentLink(isAgentExisted, agent, from) {
	await AgentLinkStorage.clear();
	if (typeof agent === "string") {
		if (!await isAgentExisted(agent)) throw new Error("Agent not existed");
		console.log("Detect and handle valid agent link:", agent, from);
		await AgentLinkStorage.setAccount(agent);
		if (from) await AgentLinkStorage.setFrom(from);
	}
}
//#endregion
//#region src/utils/version.ts
function parseVersion(version) {
	const arr = version.split(".");
	if (arr.length !== 3) throw new Error("version invalid");
	if (arr.some((el) => Number.isNaN(Number(el)))) throw new Error("version invalid");
	return arr.map((el) => Number(el));
}
function compareVersion(target, current) {
	const [currentVersion, targetVersion] = [parseVersion(current), parseVersion(target)];
	for (const i of [
		0,
		1,
		2
	]) {
		if (targetVersion[i] > currentVersion[i]) return 1;
		if (targetVersion[i] < currentVersion[i]) return -1;
	}
	return 0;
}
//#endregion
//#region src/plugins/domain.ts
const getFastDomain = async (options) => {
	throw new Error("Platform not supported");
};
const addDomain = async (domain) => {
	throw new Error("Platform not supported");
};
const deleteDomain = async (domain) => {
	throw new Error("Platform not supported");
};
const getDomainTable = async () => {
	throw new Error("Platform not supported");
};
const testDomainSpeed = async () => {
	throw new Error("Platform not supported");
};
const getPinnedDomain = async () => {
	throw new Error("Platform not supported");
};
const pinDomain = async (domain) => {
	throw new Error("Platform not supported");
};
const addTestedListener = (onTested) => {
	throw new Error("Platform not supported");
};
/**
* 檢查 ChangeDomain 的版本是否存在
*/
const checkChangeDomainVersionExisted = async () => {
	return false;
};
/**
* 一段時間後，timeout 轉導 error page
*/
const showErrorPageAfterTimeout = async (options) => {
	throw new Error("Platform not supported");
};
/**
* 取消 timeout 轉導 error page
*/
const stopErrorPageTimeout = async () => {
	throw new Error("Platform not supported");
};
/**
* 一段時間後，顯示浮在 webview 上面的按鈕
*/
const showChangeDomainAfterTimeout = async () => {
	throw new Error("Platform not supported");
};
/**
* 隱藏浮在 webview 上面的按鈕
*/
const hideChangeDomain = async () => {
	throw new Error("Platform not supported");
};
//#endregion
//#region src/plugins/device.ts
const getDeviceInfo = async () => {
	throw new Error("Platform not supported");
};
//#endregion
//#region src/modules/logToAppManager.ts
const FIRST_INSTALL_KEY = "APP:FIRST_INSTALL";
const VERSION_KEY = "APP:VERSION";
async function logIfFirstInstall(gdk, appAPI, isPWA) {
	if (!(isNativeApp() || isPWA)) return;
	const { value } = await (async () => {
		try {
			return await NativeStorage.get({ key: FIRST_INSTALL_KEY });
		} catch {
			return { value: null };
		}
	})();
	if (value) return;
	logToAppManager(gdk, appAPI, "INSTALL").then(async () => await NativeStorage.set({
		key: FIRST_INSTALL_KEY,
		value: "true"
	}));
}
async function logToAppManager(gdk, appAPI, action, account) {
	const { appId, platform, appVersion, uuid, isVirtual } = await getDeviceInfo();
	const info = `${window.navigator.userAgent} ${isVirtual ? "simulator" : ""}`;
	let clientHint = null;
	const navigator = window.navigator;
	if (navigator.userAgentData) clientHint = await getClientHintJsonString(navigator.userAgentData);
	const { uuid: sign } = await (async () => {
		if (platform !== "ios") return { uuid: void 0 };
		return await getAppSign$1();
	})();
	const ex = {};
	const { result: domainTable } = await (async () => {
		try {
			return await getDomainTable();
		} catch {
			return { result: void 0 };
		}
	})();
	if (domainTable) ex.domain = {
		table: domainTable,
		current: window.location.host
	};
	return await lastValueFrom(gdk.api.base.send({
		method: "POST",
		url: "app/1/log",
		data: {
			action,
			bid: appId,
			os: platform.toUpperCase(),
			version: appVersion,
			device: uuid,
			info,
			sign,
			account,
			clientHint,
			ex: JSON.stringify(ex)
		},
		baseURL: appAPI
	})).then(() => console.log(`Successful send ${action.toLowerCase()} log.`)).catch((e) => console.error(`Failed send ${action.toLowerCase()} log.`, e));
}
async function logIfUpgrade(gdk, appAPI) {
	if (!isNativeApp()) return false;
	const { appVersion } = await getDeviceInfo();
	const { value: savedVersion } = await (async () => {
		try {
			return await NativeStorage.get({ key: VERSION_KEY });
		} catch {
			return { value: null };
		}
	})();
	if (savedVersion && compareVersion(appVersion, savedVersion) > 0) {
		try {
			await logToAppManager(gdk, appAPI, "UPGRADE").then(async () => await NativeStorage.set({
				key: VERSION_KEY,
				value: appVersion
			}));
		} catch (e) {
			console.error("Failed to log upgrade", e);
		}
		return true;
	}
	if (!savedVersion) await NativeStorage.set({
		key: VERSION_KEY,
		value: appVersion
	});
	return false;
}
async function logAppLogin(gdk, appApi, account, isPWA) {
	if (!(isNativeApp() || isPWA)) return;
	return await logToAppManager(gdk, appApi, "LOGIN", account);
}
//#endregion
//#region src/modules/pwaRequestInterceptor.ts
const pwaRequestInterceptor = () => map((config) => {
	if (isPWA()) config.headers["X-PWA"] = "1";
	return config;
});
//#endregion
//#region src/storages/VersionTagStorage.ts
var VersionTagStorage = class extends BaseStorage {
	get versionTag() {
		if (!this.driver.exist(this.VERSION_TAG)) return null;
		return this.driver.get(this.VERSION_TAG);
	}
	set versionTag(value) {
		if (value) this.driver.set(this.VERSION_TAG, value);
		else this.driver.remove(this.VERSION_TAG);
	}
	static getInstance() {
		return super.getInstance();
	}
	prefix = "VersionTag";
	VERSION_TAG = "VERSION_TAG";
};
//#endregion
//#region src/modules/updateVersion.ts
const TIMER = 6e4;
const checkUpdateWhenNoSW = (gdk, params, thisVersion) => interval(TIMER).pipe(switchMap(() => gdk.api.fetch(...params).pipe(map(({ latestVersion }) => latestVersion === thisVersion))), filter((value) => !value), take(1), catchError((_error, source) => source));
const checkVersionTagUpdate = (payload) => {
	const lastVersionTag = VersionTagStorage.getInstance().versionTag;
	const { r, gdk, params, onUpdateFound, canRefresh } = payload;
	return fromEvent(r, "updatefound").pipe(tap(() => {
		console.log("onUpdateFound");
	}), switchMap(() => timer(0, 5e3).pipe(switchMap(() => gdk.api.fetch(...params).pipe(map(({ tag }) => tag))), filter((tag) => tag !== lastVersionTag), take(1), catchError(() => EMPTY))), filter(() => (!!r.installing || !!r.waiting) && !!r.active && navigator.serviceWorker.controller?.state === "activated"), tap(() => onUpdateFound(r.installing)), delayWhen(() => canRefresh()));
};
const checkUpdate = (r, onUpdateFound, noticeUser, onUpdating, canRefresh) => fromEvent(r, "updatefound").pipe(filter(() => !!r.installing && !!r.active && navigator.serviceWorker.controller?.state === "activated"), tap(() => {
	onUpdateFound();
}), switchMap(() => from(noticeUser())), tap(() => {
	onUpdating();
}), delayWhen(() => canRefresh()));
//#endregion
//#region src/plugins/upgrade.ts
const execUpgrade = async (url) => {
	throw new Error("Platform not supported");
};
//#endregion
//#region src/modules/verifyAndInstallApp.ts
async function getAppSign() {
	if (getNativePlatform() !== "ios") return void 0;
	try {
		return await getAppSign$1();
	} catch (e) {
		console.error(e);
		return;
	}
}
async function verifyApp(getch, urlHelper) {
	const { appId, platform, appVersion, uuid: device } = await getDeviceInfo();
	const appSign = await getAppSign();
	try {
		return (await lastValueFrom(getch.send({
			method: "GET",
			url: `app/1/verify`,
			data: {
				bid: appId,
				os: platform.toUpperCase(),
				version: appVersion,
				device,
				...appSign ? {
					sign: appSign.uuid,
					expiration: appSign.expiration
				} : {}
			},
			baseURL: urlHelper.get("appAPI"),
			timeout: 5e3
		}))).data;
	} catch (error) {
		throw new Error(`Verify app failed: ${error}`);
	}
}
async function installApp(url) {
	throw new Error("Platform not supported");
}
//#endregion
//#region src/plugins/app.ts
const openInNative = async (url) => {
	throw new Error("Platform not supported");
};
const addAppStateChangeListener = async (callback) => {
	throw new Error("Platform not supported");
};
const addAppUrlOpenListener = async (callback) => {
	throw new Error("Platform not supported");
};
const exitApp = async () => {
	throw new Error("Platform not supported");
};
//#endregion
//#region src/plugins/browser.ts
const openSharkBrowser = async (title, url, extraData) => {
	throw new Error("Platform not supported");
};
//#endregion
//#region src/plugins/clipboard.ts
const copyToClipboard = async (value) => {
	await Clipboard.write({ string: value });
};
const pasteFromClipboard = async () => {
	return await Clipboard.read();
};
//#endregion
//#region src/plugins/download.ts
const download = (url) => {
	throw new Error("Platform not supported");
};
const downloadIsSupportBase64 = () => {
	const { DownloadPlugin } = Plugins;
	if (Capacitor.isPluginAvailable("DownloadPlugin") && DownloadPlugin.downloadIsSupportBase64) return true;
	return false;
};
//#endregion
//#region src/plugins/keyboard.ts
const registerKeyboardWillShow = async (callback) => {
	return await Keyboard.addListener("keyboardWillShow", callback);
};
const registerKeyboardDidShow = async (callback) => {
	return await Keyboard.addListener("keyboardDidShow", callback);
};
const registerKeyboardWillHide = async (callback) => {
	return await Keyboard.addListener("keyboardWillHide", callback);
};
const registerKeyboardDidHide = async (callback) => {
	return await Keyboard.addListener("keyboardDidHide", callback);
};
//#endregion
//#region src/plugins/network.ts
/**
* 獲取使用者設備的 DNS 伺服器資訊
*/
const getDnsServers = async () => {
	throw new Error("Platform not supported");
};
const getNetworkType = async () => {
	throw new Error("Platform not supported");
};
/**
* 獲得測速的資料
*/
const getSpeedTestResult = async (domain) => {
	throw new Error("Platform not supported");
};
//#endregion
//#region src/plugins/sentry.ts
const initCapacitorSentry = (options, originalInit) => {
	originalInit(options);
};
//#endregion
//#region src/plugins/orientation.ts
/**
* 鎖定 App 畫面為指定的旋轉方向。
*
* 非 native 環境（PWA / 舊版 App 未內建此 plugin）會直接 no-op。
*
* @example
* ```typescript
* await lockOrientation('landscapeLeft') // 鎖橫式
* await lockOrientation('portrait')      // 鎖直式
* ```
*/
const lockOrientation = async (orientation) => {};
/**
* 解鎖畫面方向，交還系統/感應器自動旋轉。
*
* 非 native 環境會直接 no-op。
*/
const unlockOrientation = async () => {};
/**
* 取得目前畫面方向。
*
* 非 native 環境會回傳 `{ orientation: 'unspecified' }`。
*/
const getCurrentOrientation = async () => {
	return { orientation: "unspecified" };
};
//#endregion
//#region src/plugins/share.ts
const shareContent = async (options) => {
	await Share.share(options);
};
const shareFile = async (blob, filename, dialogTitle) => {};
//#endregion
//#region src/plugins/sharkLoading.ts
const notifyNativeLoading = (message) => {
	if (!isSharkBrowser()) return;
	if (window.sharkLoading) window.sharkLoading.postMessage(message);
	else if (window.webkit?.messageHandlers?.sharkLoading) window.webkit.messageHandlers.sharkLoading.postMessage(message);
};
//#endregion
//#region src/plugins/splash.ts
const hideSplashScreen = async () => {};
//#endregion
//#region src/plugins/statusbar.ts
let StatusBarStyle = /* @__PURE__ */ function(StatusBarStyle) {
	/**
	* Light text for dark backgrounds.
	*/
	StatusBarStyle["Dark"] = "DARK";
	/**
	* Dark text for light backgrounds.
	*/
	StatusBarStyle["Light"] = "LIGHT";
	return StatusBarStyle;
}({});
const setStatusBarStyle = async (style) => {
	{
		let v6Style = Style.Default;
		if (style === "DARK") v6Style = Style.Dark;
		if (style === "LIGHT") v6Style = Style.Light;
		await StatusBar.setStyle({ style: v6Style });
	}
};
//#endregion
//#region src/plugins/toast.ts
const showNativeToast = async (options) => {
	throw new Error("Platform not supported");
};
//#endregion
//#region src/storages/ActivityTapStorage.ts
const ACTIVITY_TAP_KEY = "APP:ACTIVITY_TAP";
const ActivityTapStorage = {
	async get() {
		const { value: hasPrompted } = await NativeStorage.get({ key: ACTIVITY_TAP_KEY });
		return JSON.parse(hasPrompted ?? "false");
	},
	async set(value) {
		await NativeStorage.set({
			key: ACTIVITY_TAP_KEY,
			value: JSON.stringify(value)
		});
	}
};
//#endregion
//#region src/storages/AppLoginStorage.ts
const AppLoginStorage = (() => {
	const IS_AUTO_LOGIN_KEY = "ga-app-is-auto-login";
	const ACCOUNT_KEY = "ga-app-account";
	const DEVICE_KEY = "ga-app-device";
	const OLD_PASSWORD_KEY = "ga-app-password";
	const NEW_REFRESH_TOKEN_KEY = "ga-app-refresh-token";
	const NEW_REFRESH_TTL_KEY = "ga-app-refresh-ttl";
	const TIME_ENCODE_KEY = 3428947892334;
	const getPlugins = () => {
		throw new Error("Platform not supported");
	};
	const getAccount = async () => {
		const { SecureStoragePlugin, isAvailable } = getPlugins();
		if (!isAvailable()) return "";
		try {
			const { value: account } = await SecureStoragePlugin.get({ key: ACCOUNT_KEY });
			return account;
		} catch {
			return "";
		}
	};
	const getOldPassword = async () => {
		const { SecureStoragePlugin, isAvailable } = getPlugins();
		if (!isAvailable()) return "";
		try {
			const { value: password } = await SecureStoragePlugin.get({ key: OLD_PASSWORD_KEY });
			return password;
		} catch {
			return "";
		}
	};
	const getDeviceUUID = async () => {
		const { SecureStoragePlugin } = getPlugins();
		try {
			const { value } = await SecureStoragePlugin.get({ key: DEVICE_KEY });
			if (value) return value;
			throw new Error("No uuid");
		} catch {
			const { uuid } = await getDeviceInfo();
			await SecureStoragePlugin.set({
				key: DEVICE_KEY,
				value: uuid
			});
			return uuid;
		}
	};
	const getRefreshToken = async () => {
		const { SecureStoragePlugin, isAvailable } = getPlugins();
		if (!isAvailable()) return "";
		try {
			const { value } = await SecureStoragePlugin.get({ key: NEW_REFRESH_TOKEN_KEY });
			return value;
		} catch {
			return "";
		}
	};
	return {
		getAccount,
		setAccount: async (account) => {
			const { SecureStoragePlugin, isAvailable } = getPlugins();
			if (!isAvailable()) return;
			await SecureStoragePlugin.set({
				key: ACCOUNT_KEY,
				value: account
			});
		},
		async isWantedAutoLogin() {
			const { isAvailable } = getPlugins();
			if (!isAvailable()) return false;
			try {
				const { value: active } = await NativeStorage.get({ key: IS_AUTO_LOGIN_KEY });
				return JSON.parse(active ?? "false");
			} catch {
				return false;
			}
		},
		async isCanAutoLogin() {
			const { SecureStoragePlugin, isAvailable } = getPlugins();
			if (!isAvailable()) return false;
			try {
				const { value: ttlString } = await SecureStoragePlugin.get({ key: NEW_REFRESH_TTL_KEY });
				const ttl = !Number.isNaN(Number(ttlString)) ? Number(ttlString) : 0;
				const token = await getRefreshToken();
				return NowTime.get().valueOf() < ttl && token !== "";
			} catch {
				return false;
			}
		},
		getDeviceUUID,
		async getSignArgs() {
			return [
				(await getAccount()).toLowerCase(),
				await getDeviceUUID(),
				NowTime.get().valueOf() + TIME_ENCODE_KEY
			];
		},
		async enableAutoLogin(ttl) {
			const { SecureStoragePlugin, isAvailable } = getPlugins();
			if (!isAvailable()) return;
			await Promise.all([NativeStorage.set({
				key: IS_AUTO_LOGIN_KEY,
				value: "true"
			}), SecureStoragePlugin.set({
				key: NEW_REFRESH_TTL_KEY,
				value: JSON.stringify(ttl)
			})]);
		},
		async saveRefreshToken(token) {
			const { SecureStoragePlugin } = getPlugins();
			if (!token) return;
			await SecureStoragePlugin.set({
				key: NEW_REFRESH_TOKEN_KEY,
				value: token
			});
		},
		getRefreshToken,
		async disableAutoLogin() {
			const { isAvailable } = getPlugins();
			if (!isAvailable()) return;
			return await NativeStorage.set({
				key: IS_AUTO_LOGIN_KEY,
				value: "false"
			});
		},
		async removeAutoLogin() {
			const { SecureStoragePlugin, isAvailable } = getPlugins();
			if (!isAvailable()) return;
			try {
				await NativeStorage.set({
					key: IS_AUTO_LOGIN_KEY,
					value: "false"
				});
			} catch {}
			try {
				await SecureStoragePlugin.remove({ key: NEW_REFRESH_TTL_KEY });
			} catch {}
			try {
				await SecureStoragePlugin.remove({ key: NEW_REFRESH_TOKEN_KEY });
			} catch {}
		},
		isSetOldPassword: async () => {
			const { SecureStoragePlugin, isAvailable } = getPlugins();
			if (!isAvailable()) return false;
			try {
				await SecureStoragePlugin.get({ key: OLD_PASSWORD_KEY });
				return true;
			} catch {
				return false;
			}
		},
		getOldPassword,
		removeOldAutoLogin: async () => {
			const { SecureStoragePlugin, isAvailable } = getPlugins();
			if (!isAvailable()) return;
			await SecureStoragePlugin.remove({ key: OLD_PASSWORD_KEY });
		},
		getOldAutoLogin: async () => ({
			account: await getAccount(),
			password: await getOldPassword()
		})
	};
})();
//#endregion
//#region src/storages/DomainStorage.ts
const DOMAIN_KEY = "APP:DOMAINS";
const DomainStorage = {
	async get() {
		const dateInDisk = await NativeStorage.get({ key: DOMAIN_KEY }).catch(() => ({ value: null }));
		return JSON.parse(dateInDisk.value ?? JSON.stringify({ domains: [] }));
	},
	async set(value) {
		await NativeStorage.set({
			key: DOMAIN_KEY,
			value: JSON.stringify(value)
		});
	}
};
//#endregion
//#region src/storages/FastBindingDialogStorage.ts
const FAST_BINDING_DIALOG_KEY = "APP:FAST_BINDING_DIALOG_SHOWN";
const FastBindingDialogStorage = {
	async get() {
		const { value: hasShown } = await NativeStorage.get({ key: FAST_BINDING_DIALOG_KEY });
		return JSON.parse(hasShown ?? "false");
	},
	async set(value) {
		await NativeStorage.set({
			key: FAST_BINDING_DIALOG_KEY,
			value: JSON.stringify(value)
		});
	}
};
//#endregion
//#region src/storages/LiveBetSourceStorage.ts
const LIVE_BET_SOURCE_KEY = "APP:LIVE_BET_SOURCE";
const SOURCES = ["pm", "fb"];
const LiveBetSourceStorage = {
	async get() {
		const { value } = await NativeStorage.get({ key: LIVE_BET_SOURCE_KEY });
		if (!value) return null;
		const source = JSON.parse(value);
		return SOURCES.includes(source) ? source : null;
	},
	async set(value) {
		await NativeStorage.set({
			key: LIVE_BET_SOURCE_KEY,
			value: JSON.stringify(value)
		});
	}
};
//#endregion
//#region src/storages/StartupStorage.ts
var StartupStorage = class extends BaseSessionStorage {
	get launch() {
		if (!this.driver.exist(this.FIRST)) return false;
		return JSON.parse(this.driver.get(this.FIRST));
	}
	set launch(value) {
		if (value) this.driver.set(this.FIRST, JSON.stringify(value));
		else this.driver.remove(this.FIRST);
	}
	get downloadBannerClosed() {
		if (!this.driver.exist(this.DOWNLOAD_BANNER_CLOSED)) return false;
		return JSON.parse(this.driver.get(this.DOWNLOAD_BANNER_CLOSED));
	}
	set downloadBannerClosed(value) {
		if (value) this.driver.set(this.DOWNLOAD_BANNER_CLOSED, JSON.stringify(value));
		else this.driver.remove(this.DOWNLOAD_BANNER_CLOSED);
	}
	static getInstance() {
		return super.getInstance();
	}
	prefix = "STARTUP";
	FIRST = "LAUNCH";
	DOWNLOAD_BANNER_CLOSED = "DOWNLOADBANNERCLOSED";
};
//#endregion
//#region src/storages/UpgradeStorage.ts
const UPGRADE_KEY = "APP:UPGRADE";
const KEY = {
	MAIN: `${UPGRADE_KEY}:MAIN`,
	NOTICE: `${UPGRADE_KEY}:NOTICE`
};
const UpgradeStorage = /* @__PURE__ */ (() => {
	async function getAvailableVersion() {
		if (!isNativeApp()) return null;
		return (await NativeStorage.get({ key: KEY.MAIN })).value;
	}
	return {
		async saveAvailable(version) {
			if (!isNativeApp()) throw new Error("only available in native app");
			if (await getAvailableVersion() === version) return;
			await NativeStorage.set({
				key: KEY.MAIN,
				value: version
			});
			await NativeStorage.set({
				key: KEY.NOTICE,
				value: version
			});
		},
		async saveUnavailable() {
			if (!isNativeApp()) throw new Error("only available in native app");
			try {
				await NativeStorage.remove({ key: KEY.MAIN });
				await NativeStorage.remove({ key: KEY.NOTICE });
			} catch {}
		},
		async getAvailable() {
			return await getAvailableVersion() !== null;
		},
		async getNotice() {
			if (!isNativeApp()) return false;
			return (await NativeStorage.get({ key: KEY.NOTICE })).value !== null;
		},
		async removeNotice() {
			if (!isNativeApp()) return;
			await NativeStorage.remove({ key: KEY.NOTICE });
		}
	};
})();
//#endregion
//#region src/utils/appGAEvent.ts
let AppGAEventName = /* @__PURE__ */ function(AppGAEventName) {
	AppGAEventName["CHANGE_DOMAIN_CLICK_LAUNCH_BUTTON"] = "change_domain_click_launch_button";
	AppGAEventName["CHANGE_DOMAIN_CLICK_NAVBAR_BUTTON"] = "change_domain_click_navbar_button";
	AppGAEventName["CHANGE_DOMAIN_CLICK_NETWORK_PAGE_BUTTON"] = "change_domain_click_network_page_button";
	AppGAEventName["CHANGE_DOMAIN_CLICK_REFRESH"] = "change_domain_click_refresh";
	AppGAEventName["CHANGE_DOMAIN_BY_LINK"] = "change_domain_by_link";
	AppGAEventName["CHANGE_DOMAIN_SUCCESS_DURATION_MS"] = "change_domain_success_duration_ms";
	AppGAEventName["CHANGE_DOMAIN_FAIL_DURATION_MS"] = "change_domain_fail_duration_ms";
	return AppGAEventName;
}({});
async function getBaseParams() {
	const [deviceInfo, dnsInfo, deviceId] = await Promise.all([
		getDeviceInfo(),
		getDnsServers(),
		AppLoginStorage.getDeviceUUID()
	]);
	return {
		mobile_app_version: deviceInfo.appVersion,
		client_ip: dnsInfo.neteaseIp || dnsInfo.qqIp,
		device_id: deviceId,
		timestamp: Date.now()
	};
}
//#endregion
//#region src/utils/sleep.ts
async function sleep(ms) {
	return await new Promise((resolve) => setTimeout(resolve, ms));
}
//#endregion
export { ActivityTapStorage, AgentLinkStorage, AppGAEventName, AppLoginStorage, DomainStorage, FastBindingDialogStorage, LiveBetSourceStorage, NativeStorage, StartupStorage, StatusBarStyle, UpgradeStorage, VersionTagStorage, addAppStateChangeListener, addAppUrlOpenListener, addDomain, addTestedListener, appAgentLinkGuard, appLoaded, checkChangeDomainVersionExisted, checkUpdate, checkUpdateWhenNoSW, checkVersionTagUpdate, compareVersion, copyToClipboard, deleteDomain, download, downloadIsSupportBase64, execUpgrade, exitApp, getAppLinkHost, getAppSign, getBaseParams, getClientHintJsonString, getCurrentOrientation, getDeviceInfo, getDnsServers, getDomainTable, getFastDomain, getNativePlatform, getNetworkType, getPinnedDomain, getSafariVersion, getSpeedTestResult, handleAgentLink, hideChangeDomain, hideSplashScreen, initCapacitorSentry, initSW, installApp, isIOSDevice, isNativeApp, isPWA, isSharkBrowser, lockOrientation, logAppLogin, logIfFirstInstall, logIfUpgrade, notifyNativeLoading, openInNative, openSharkBrowser, parseVersion, pasteFromClipboard, pinDomain, pwaRequestInterceptor, registerKeyboardDidHide, registerKeyboardDidShow, registerKeyboardWillHide, registerKeyboardWillShow, setBackGestures, setStatusBarStyle, shareContent, shareFile, showChangeDomainAfterTimeout, showErrorPageAfterTimeout, showNativeToast, sleep, stopErrorPageTimeout, testDomainSpeed, unlockOrientation, verifyApp };

//# sourceMappingURL=index.js.map